import React from 'react';
import { ShoppingCart } from 'lucide-react';

/**
 * Club_CartBar — barra inferior del POS con el total del carrito.
 *
 * Contrapartida en código del componente de Figma (3185:10160), con sus dos
 * variantes de la propiedad `Credit`:
 *
 *   Credit=Default        nada que señalar
 *   Credit=insufficient   la compra deja al socio en negativo
 *
 * OJO con el frame: pinta el TOTAL en rojo cuando no llega el saldo. Aquí no,
 * y por dos razones. Una, un total no es un número negativo: es lo que cuesta
 * la compra, y vale lo mismo haya saldo o no. Dos, el saldo negativo está
 * permitido en el club, así que no hay error que señalar. Lo que se dice es en
 * cuánto queda el socio, en la línea de abajo y con su signo.
 *
 * Medidas del frame: barra a ancho completo, 68 de alto, padding 12, borde
 * SOLO arriba. Sin radio y sin sombra — no es una tarjeta flotante.
 */
export default function CartBar({
  total = 0,
  count = 0,
  /** Saldo del socio. Si no se pasa (compra sin socio asignado), no se evalúa. */
  credit = null,
  onViewCart,
  /** Empuje de la animación al añadir producto, para no perder el gesto actual. */
  animating = false,
  className = '',
}) {
  const insufficient = credit != null && total > credit;

  return (
    <div
      data-testid="cart-bar"
      data-credit={insufficient ? 'insufficient' : 'default'}
      className={`flex h-[68px] items-center justify-between gap-2 border-t border-[var(--border)] bg-[var(--background)] px-3 ${className}`}
    >
      <div className="min-w-0">
        {/* El total NO se pinta de rojo. Un total no es un número negativo: es
            lo que cuesta la compra, y sale igual haya saldo o no. Pintarlo en
            rojo por «no llega el saldo» convertía un dato en un error —y el
            saldo negativo está permitido en el club, así que ni siquiera hay
            error que señalar—. Lo que sí cambia es la segunda línea, que dice
            con cuánto se queda el socio. */}
        <div className="text-lg font-bold text-[var(--foreground)]">
          Total: €{Number(total).toFixed(2)}
        </div>
        <div className="text-sm text-[var(--muted-foreground)]">
          {count} {count === 1 ? 'producto añadido' : 'productos añadidos'}
          {insufficient && (
            <>
              {' · '}
              <span className="text-[var(--destructive)]">
                queda en −€{Math.abs(credit - total).toFixed(2)}
              </span>
            </>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={onViewCart}
        className={`inline-flex h-9 flex-shrink-0 items-center gap-2 rounded-sm bg-[var(--primary)] px-4 text-sm font-medium text-[var(--primary-foreground)] transition-transform duration-75 hover:bg-[var(--primary-strong)] active:scale-[0.97] ${
          animating ? 'scale-105' : ''
        }`}
      >
        <ShoppingCart
          size={16}
          strokeWidth={1.5}
          className={`transition-transform duration-75 ${animating ? 'scale-125 rotate-12' : ''}`}
        />
        Ver carrito
      </button>
    </div>
  );
}
